// =============================================================================
// DesignPanel — the site design on a BID or project (31 Aug 2026)
// =============================================================================
// James: "a design tab in projects / BIDs that has the design layout, RF
// (sector name, freq, height, azimuth, mechanical tilt, electrical tilt,
// active vs passive with RRUs), ground cabinets or shelter type and qty,
// battery racks and strings, Tx has MW links or fibre, US and DS sites, link
// freq, channels, Hi/Lo, dual band, azimuth, height, MW dish size. We won't
// always have all this info but we should have a place for it when we do.
// Manual inputs or a direct fill by Claude with a template - a download
// template anyone can use - then we load it in."
//
// Three ways in, one importer:
//   1. Type it        - Edit -> tables with inputs -> Save.
//   2. Excel template - Download (pre-filled with what we know) -> fill by
//                       hand or hand it to an AI with the design docs ->
//                       Import the file back.
//   3. Paste          - the Coretel Site Scope Summary (coretel.scope.v1) or
//                       our own JSON (coretel.design.v1) from any AI chat.
// Every import lands in EDIT mode for review - nothing saves until Save.
// The raw payload is kept on the design (raw_import) so it's auditable.
//
// A design attaches to a BID or a project. When a BID is promoted the project
// tab finds the BID's design and adopts it on first save (sets project_id),
// so nothing has to be re-entered.
// =============================================================================

import React, { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  RadioTower, Server, Cable, Upload, Clipboard, Pencil, Save, X, Plus, Trash2,
  FileSpreadsheet, DraftingCompass, FileText, ChevronDown, ChevronRight,
} from "lucide-react";
import * as XLSX from "xlsx";
import { supabase as supabaseTyped } from "@/lib/supabase";
// The design tables post-date the generated types (regen queued) - same cast
// the site_roster work uses.
const supabase = supabaseTyped as any;
import { Card, Button, Spinner, Textarea } from "@/components/ui";

// ---- parent ------------------------------------------------------------------
export type DesignParent =
  | { kind: "project"; id: string; code?: string | null; name?: string | null; siteId?: string | null }
  | { kind: "bid"; id: string; code?: string | null; name?: string | null; siteId?: string | null };

// ---- column model (drives header, inputs, view and import) -------------------
type Col = {
  key: string;
  label: string;
  kind?: "text" | "num" | "int" | "select" | "bool";
  opts?: string[];
  w?: string;          // tailwind min width for the input cell
  mono?: boolean;
  aliases?: string[];  // extra header spellings accepted on import
};

const WORKS = ["NEW", "REMOVE", "RELOCATE", "RETAIN", "REPLACE"];

const RF_COLS: Col[] = [
  { key: "sector",         label: "Sector",          w: "w-16",  aliases: ["sector name"] },
  { key: "works",          label: "Works",           kind: "select", opts: WORKS, w: "w-24" },
  { key: "antenna_model",  label: "Antenna model",   w: "w-36",  aliases: ["antenna", "model"] },
  { key: "qty",            label: "Qty",             kind: "int", w: "w-14" },
  { key: "band",           label: "Band / freq",     w: "w-28",  aliases: ["freq", "frequency", "band"] },
  { key: "height_m",       label: "Height (m)",      kind: "num", w: "w-20", aliases: ["height", "height agl (m)", "height agl"] },
  { key: "azimuth_deg",    label: "Azimuth (°T)",    kind: "num", w: "w-20", aliases: ["azimuth", "az"] },
  { key: "mech_tilt",      label: "Mech tilt",       kind: "num", w: "w-18", aliases: ["mechanical tilt", "mtilt"] },
  { key: "elec_tilt",      label: "Elec tilt",       w: "w-18",  aliases: ["electrical tilt", "etilt"] },
  { key: "active_passive", label: "Active / passive", kind: "select", opts: ["active", "passive"], w: "w-24", aliases: ["active/passive", "active vs passive", "type"] },
  { key: "rru_model",      label: "RRU model",       w: "w-28",  aliases: ["rru", "radio", "radio model"] },
  { key: "rru_qty",        label: "RRU qty",         kind: "int", w: "w-16", aliases: ["rrus", "radio qty"] },
  { key: "mount",          label: "Mount",           w: "w-24" },
  { key: "notes",          label: "Notes",           w: "w-40" },
];

const GROUND_TYPES = ["cabinet", "shelter", "battery_rack", "battery_string", "rectifier", "pdb", "gps", "other"];
const GROUND_COLS: Col[] = [
  { key: "item_type",      label: "Item",            kind: "select", opts: GROUND_TYPES, w: "w-32", aliases: ["item type", "type"] },
  { key: "works",          label: "Works",           kind: "select", opts: WORKS, w: "w-24" },
  { key: "model",          label: "Model / type",    w: "w-36",  aliases: ["model", "cabinet type", "shelter type"] },
  { key: "indoor_outdoor", label: "In / out",        kind: "select", opts: ["indoor", "outdoor"], w: "w-20", aliases: ["indoor/outdoor", "indoor / outdoor"] },
  { key: "qty",            label: "Qty",             kind: "int", w: "w-14" },
  { key: "strings",        label: "Strings",         kind: "int", w: "w-16", aliases: ["battery strings"] },
  { key: "ah",             label: "Ah",              kind: "num", w: "w-16", aliases: ["ah per string", "capacity"] },
  { key: "voltage",        label: "V",               kind: "num", w: "w-14", aliases: ["volts", "voltage"] },
  { key: "location",       label: "Location",        w: "w-28" },
  { key: "notes",          label: "Notes",           w: "w-40" },
];

const TX_COLS: Col[] = [
  { key: "link_name",    label: "Link",            w: "w-32",  aliases: ["link name", "link"] },
  { key: "medium",       label: "Medium",          kind: "select", opts: ["microwave", "fibre", "other"], w: "w-24", aliases: ["type", "mw/fibre"] },
  { key: "works",        label: "Works",           kind: "select", opts: WORKS, w: "w-24" },
  { key: "us_site",      label: "US site",         w: "w-32",  aliases: ["upstream", "upstream site", "us", "far-end site", "far end site"] },
  { key: "ds_site",      label: "DS site",         w: "w-32",  aliases: ["downstream", "downstream site", "ds"] },
  { key: "freq_band",    label: "Freq band",       w: "w-20",  aliases: ["freq", "frequency", "band", "link freq"] },
  { key: "channels",     label: "Ch",              kind: "int", w: "w-12", aliases: ["channels", "how many channels"] },
  { key: "hi_lo",        label: "Hi / Lo",         kind: "select", opts: ["HI", "LO"], w: "w-16", aliases: ["hi/lo", "hrlb", "high/low"] },
  { key: "dual_band",    label: "Dual band",       kind: "bool", w: "w-16" },
  { key: "azimuth_deg",  label: "Azimuth (°T)",    kind: "num", w: "w-20", aliases: ["azimuth", "az"] },
  { key: "height_m",     label: "Height (m)",      kind: "num", w: "w-20", aliases: ["height", "height agl (m)"] },
  { key: "dish_m",       label: "Dish (m)",        kind: "num", w: "w-16", aliases: ["dish", "dish size", "dish diameter (m)", "mw dish size"] },
  { key: "polarisation", label: "Pol",             w: "w-16",  aliases: ["polarisation", "polarization"] },
  { key: "odu_idu",      label: "ODU / IDU",       w: "w-24",  aliases: ["odu/idu", "odu", "idu"] },
  { key: "notes",        label: "Notes",           w: "w-40" },
];

const HEADER_FIELDS: { key: string; label: string; kind?: "text" | "num" | "int" | "date"; wide?: boolean; aliases?: string[] }[] = [
  { key: "design_ref",         label: "Design ref",           aliases: ["design reference", "doc no", "document"] },
  { key: "design_rev",         label: "Revision",             aliases: ["rev", "design revision"] },
  { key: "design_date",        label: "Design date",          kind: "date", aliases: ["date"] },
  { key: "structure_type",     label: "Structure",            aliases: ["structure type"] },
  { key: "structure_height_m", label: "Structure height (m)", kind: "num", aliases: ["structure height", "height (m)"] },
  { key: "site_type",          label: "Site type" },
  { key: "sector_count",       label: "Sectors",              kind: "int", aliases: ["sector count", "no. sectors"] },
  { key: "sector_naming",      label: "Sector naming",        wide: true, aliases: ["naming convention", "sector naming convention"] },
  { key: "summary",            label: "Scope in one breath",  wide: true, aliases: ["scope", "scope summary"] },
  { key: "layout_notes",       label: "Layout notes",         wide: true, aliases: ["layout", "design layout"] },
  { key: "power_notes",        label: "Power & earthing",     wide: true, aliases: ["power", "power notes", "earthing"] },
  { key: "civil_notes",        label: "Civil & access",       wide: true, aliases: ["civil", "civil notes", "access"] },
];

// ---- draft rows are all strings (they back <input>s); typed on save ----------
type Row = Record<string, string> & { _k: string };
type Draft = { header: Record<string, string>; rf: Row[]; ground: Row[]; tx: Row[] };

let keySeq = 0;
const newKey = () => `k${Date.now().toString(36)}${(keySeq++).toString(36)}`;
const blankRow = (cols: Col[], defaults: Record<string, string> = {}): Row => {
  const r: Row = { _k: newKey() };
  for (const c of cols) r[c.key] = defaults[c.key] ?? "";
  return r;
};
const s = (v: any) => (v === null || v === undefined ? "" : String(v));

function rowsFromDb(cols: Col[], rows: any[]): Row[] {
  return rows.map(r => {
    const out: Row = { _k: newKey() };
    for (const c of cols) out[c.key] = c.kind === "bool" ? (r[c.key] === true ? "yes" : r[c.key] === false ? "no" : "") : s(r[c.key]);
    return out;
  });
}

function typed(cols: Col[], row: Row): Record<string, any> {
  const out: Record<string, any> = {};
  for (const c of cols) {
    const raw = (row[c.key] ?? "").trim();
    if (c.kind === "num" || c.kind === "int") {
      const n = raw === "" ? NaN : Number(raw.replace(/[^0-9.\-]/g, ""));
      out[c.key] = Number.isFinite(n) ? (c.kind === "int" ? Math.round(n) : n) : null;
    } else if (c.kind === "bool") {
      out[c.key] = raw === "" ? null : /^(y|yes|true|1|dual)/i.test(raw);
    } else if (c.kind === "select") {
      const hit = (c.opts ?? []).find(o => o.toLowerCase() === raw.toLowerCase());
      out[c.key] = hit ?? null;
    } else {
      out[c.key] = raw === "" ? null : raw;
    }
  }
  return out;
}

const rowIsEmpty = (cols: Col[], row: Row) => cols.every(c => (row[c.key] ?? "").trim() === "");

// ---- import: normalise a foreign value into what a column accepts ------------
const norm = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

function coerce(c: Col, v: any): string {
  const raw = s(v).trim();
  if (raw === "") return "";
  if (c.kind === "select") {
    const n = norm(raw);
    const direct = (c.opts ?? []).find(o => norm(o) === n);
    if (direct) return direct;
    // friendly spellings
    if (c.key === "item_type") {
      if (/battery.*string|string/.test(n)) return "battery_string";
      if (/battery.*rack|rack/.test(n)) return "battery_rack";
      if (/battery/.test(n)) return "battery_string";
      if (/cabinet|odc|rbs/.test(n)) return "cabinet";
      if (/shelter|hut|room/.test(n)) return "shelter";
      if (/rectifier|psu|power supply/.test(n)) return "rectifier";
      if (/pdb|distribution|breaker/.test(n)) return "pdb";
      if (/gps/.test(n)) return "gps";
      return "other";
    }
    if (c.key === "works") {
      if (/^(install|new|add)/.test(n)) return "NEW";
      if (/^(remove|decom|dismantle)/.test(n)) return "REMOVE";
      if (/^(reloc|move)/.test(n)) return "RELOCATE";
      if (/^(retain|exist|keep)/.test(n)) return "RETAIN";
      if (/^(replace|swap)/.test(n)) return "REPLACE";
      return "";
    }
    if (c.key === "medium") {
      if (/mw|micro/.test(n)) return "microwave";
      if (/fib/.test(n)) return "fibre";
      return "other";
    }
    if (c.key === "hi_lo") {
      if (/^h/.test(n)) return "HI";
      if (/^l/.test(n)) return "LO";
      return "";
    }
    if (c.key === "active_passive") {
      if (/^a|rru|active/.test(n)) return "active";
      if (/^p|passive/.test(n)) return "passive";
      return "";
    }
    if (c.key === "indoor_outdoor") {
      if (/^in/.test(n)) return "indoor";
      if (/^out/.test(n)) return "outdoor";
      return "";
    }
    return "";
  }
  if (c.kind === "bool") return /^(y|yes|true|1|dual)/i.test(raw) ? "yes" : /^(n|no|false|0)/i.test(raw) ? "no" : "";
  return raw;
}

// Dates arrive three ways: an Excel serial number (45951), an Australian
// dd/mm/yyyy string, or ISO. Postgres only takes ISO - 31 Aug 2026 live
// import failed on 'invalid input syntax for type date: "45951"'.
function toIsoDate(v: any): string {
  if (v === null || v === undefined || v === "") return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  if (typeof v === "number" && v > 20000 && v < 80000) {
    const d = XLSX.SSF.parse_date_code(v);
    if (d) return `${d.y}-${pad(d.m)}-${pad(d.d)}`;
  }
  const str = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  if (/^\d{5}$/.test(str)) return toIsoDate(Number(str));
  const au = str.match(/^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{2,4})$/);
  if (au) {
    let y = Number(au[3]); if (y < 100) y += 2000;
    return `${y}-${pad(Number(au[2]))}-${pad(Number(au[1]))}`;
  }
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

function colByHeader(cols: Col[], header: string): Col | undefined {
  const h = norm(header);
  if (!h) return undefined;
  return cols.find(c => norm(c.label) === h || norm(c.key) === h || (c.aliases ?? []).some(a => norm(a) === h));
}

function sheetToRows(cols: Col[], ws: XLSX.WorkSheet | undefined): Row[] {
  if (!ws) return [];
  const grid: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: "" });
  if (grid.length < 2) return [];
  const map = grid[0].map((h: any) => colByHeader(cols, s(h)));
  const out: Row[] = [];
  for (const line of grid.slice(1)) {
    const r = blankRow(cols);
    map.forEach((c: Col | undefined, i: number) => { if (c) r[c.key] = coerce(c, line[i]); });
    if (!rowIsEmpty(cols, r)) out.push(r);
  }
  return out;
}

function siteSheetToHeader(ws: XLSX.WorkSheet | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!ws) return out;
  const grid: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: "" });
  for (const line of grid) {
    const label = norm(s(line[0]));
    const val = s(line[1]).trim();
    if (!label || !val) continue;
    const f = HEADER_FIELDS.find(h => norm(h.label) === label || norm(h.key) === label || (h.aliases ?? []).some(a => norm(a) === label));
    if (f) out[f.key] = f.kind === "date" ? toIsoDate(line[1]) : val;
  }
  return out;
}

// coretel.scope.v1 (the Cowork directive) and coretel.design.v1 (our own)
function jsonToDraft(obj: any): { draft: Partial<Draft>; note: string } | null {
  if (!obj || typeof obj !== "object") return null;
  const schema = String(obj.schema ?? "");
  if (schema.startsWith("coretel.design")) {
    const h: Record<string, string> = {};
    for (const f of HEADER_FIELDS) if (obj.header?.[f.key] != null) h[f.key] = f.kind === "date" ? toIsoDate(obj.header[f.key]) : s(obj.header[f.key]);
    const rows = (cols: Col[], arr: any[]) => (Array.isArray(arr) ? arr : []).map(o => {
      const r = blankRow(cols); for (const c of cols) r[c.key] = coerce(c, o?.[c.key]); return r;
    }).filter(r => !rowIsEmpty(cols, r));
    return { draft: { header: h, rf: rows(RF_COLS, obj.rf), ground: rows(GROUND_COLS, obj.ground), tx: rows(TX_COLS, obj.tx) }, note: "Coretel design JSON" };
  }
  if (schema.startsWith("coretel.scope") || obj.site || obj.antennas) {
    const site = obj.site ?? {};
    const h: Record<string, string> = {
      design_ref: s(site.design_ref), design_rev: s(site.design_rev), design_date: toIsoDate(site.design_date),
      structure_type: s(site.structure_type), structure_height_m: s(site.structure_height_m),
      site_type: s(site.site_type), sector_count: s(site.sector_count), sector_naming: s(site.sector_naming),
      summary: s(obj.summary),
      power_notes: obj.power_earthing ? Object.entries(obj.power_earthing).filter(([, v]) => v).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join("\n") : "",
      civil_notes: obj.civil_access ? Object.entries(obj.civil_access).filter(([, v]) => v).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join("\n") : "",
    };
    const rf: Row[] = [];
    for (const a of (Array.isArray(obj.antennas) ? obj.antennas : [])) {
      const r = blankRow(RF_COLS);
      r.sector = s(a.sector); r.works = coerce(RF_COLS[1], a.works); r.antenna_model = s(a.model); r.qty = s(a.qty);
      r.band = s(a.band); r.height_m = s(a.height_m); r.azimuth_deg = s(a.azimuth_deg); r.mech_tilt = s(a.mech_tilt);
      r.elec_tilt = s(a.elec_tilt); r.mount = s(a.mount); r.notes = s(a.notes);
      rf.push(r);
    }
    // radios: attach to the first antenna row in the same sector, else own row
    for (const rd of (Array.isArray(obj.radios) ? obj.radios : [])) {
      const host = rf.find(r => r.sector === s(rd.sector) && !r.rru_model);
      if (host) { host.rru_model = s(rd.model); host.rru_qty = s(rd.qty); host.active_passive = "active"; if (!host.band) host.band = s(rd.band); }
      else {
        const r = blankRow(RF_COLS);
        r.sector = s(rd.sector); r.works = coerce(RF_COLS[1], rd.works); r.rru_model = s(rd.model); r.rru_qty = s(rd.qty);
        r.band = s(rd.band); r.active_passive = "active"; r.notes = s(rd.notes); rf.push(r);
      }
    }
    const tx: Row[] = (Array.isArray(obj.mw_links) ? obj.mw_links : []).map((l: any) => {
      const r = blankRow(TX_COLS);
      r.link_name = s(l.link_name); r.medium = "microwave"; r.works = coerce(TX_COLS[2], l.works); r.us_site = s(l.far_end_site);
      r.freq_band = s(l.band); r.azimuth_deg = s(l.azimuth_deg); r.height_m = s(l.height_m); r.dish_m = s(l.dish_m);
      r.polarisation = s(l.polarisation); r.odu_idu = s(l.odu_idu); r.notes = s(l.notes);
      return r;
    });
    const ground: Row[] = (Array.isArray(obj.ground) ? obj.ground : []).map((g: any) => {
      const r = blankRow(GROUND_COLS);
      r.item_type = coerce(GROUND_COLS[0], g.item); r.works = coerce(GROUND_COLS[1], g.works); r.model = s(g.model);
      r.indoor_outdoor = coerce(GROUND_COLS[3], g.indoor_outdoor); r.qty = s(g.qty); r.strings = s(g.strings); r.ah = s(g.ah);
      r.voltage = s(g.voltage); r.location = s(g.location); r.notes = s(g.notes);
      return r;
    });
    return { draft: { header: h, rf, ground, tx }, note: "Coretel Site Scope Summary" };
  }
  return null;
}

function extractJson(text: string): any | null {
  const fenced = text.match(/```json\s*([\s\S]*?)```/i);
  const candidates = [fenced?.[1], text, text.slice(text.indexOf("{"), text.lastIndexOf("}") + 1)];
  for (const c of candidates) {
    if (!c) continue;
    try { return JSON.parse(c); } catch { /* next */ }
  }
  return null;
}

// ---- Excel template (pre-filled with what we already know) -------------------
function downloadTemplate(parent: DesignParent, draft: Draft) {
  const wb = XLSX.utils.book_new();
  const readme = [
    ["Coretel site design template"],
    [""],
    ["Fill what the design documents give you. Leave a cell blank if the docs don't say - never guess."],
    ["Heights are metres above ground. Azimuths are degrees TRUE. One row per antenna / cabinet / link."],
    ["Works: NEW, REMOVE, RELOCATE, RETAIN, REPLACE.   Active/passive: active (has RRUs), passive."],
    ["Ground Item: cabinet, shelter, battery_rack, battery_string, rectifier, pdb, gps, other."],
    ["Tx Medium: microwave, fibre, other.   Hi/Lo: HI or LO.   Dual band: yes / no."],
    ["You can hand this file plus the FC/design PDFs to an AI and ask it to fill the sheets - then Import it in Ops."],
    ["Don't rename the sheets or the header row - Ops matches on them."],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(readme), "Read me");

  const site = [["Field", "Value"], ["Job", `${parent.code ?? ""} ${parent.name ?? ""}`.trim()]];
  for (const f of HEADER_FIELDS) site.push([f.label, draft.header[f.key] ?? ""]);
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(site), "Site");

  const table = (cols: Col[], rows: Row[]) => {
    const aoa: any[][] = [cols.map(c => c.label)];
    for (const r of rows) aoa.push(cols.map(c => r[c.key] ?? ""));
    if (rows.length === 0) aoa.push(cols.map(() => ""));
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = cols.map(c => ({ wch: Math.max(10, c.label.length + 4) }));
    return ws;
  };
  XLSX.utils.book_append_sheet(wb, table(RF_COLS, draft.rf), "RF");
  XLSX.utils.book_append_sheet(wb, table(GROUND_COLS, draft.ground), "Ground");
  XLSX.utils.book_append_sheet(wb, table(TX_COLS, draft.tx), "Tx");

  const stem = (parent.code ?? parent.kind).replace(/[^A-Za-z0-9_-]+/g, "_");
  XLSX.writeFile(wb, `Coretel_Design_${stem}.xlsx`);
}

// =============================================================================
export default function DesignPanel({ parent, canEdit, embedded = false }: {
  parent: DesignParent; canEdit: boolean; embedded?: boolean;
}) {
  // James: "make this table a little wider to see it all". The project page
  // sits in a centred column; the tables step out of it on big screens.
  const wide = !embedded && parent.kind === "project";
  const qc = useQueryClient();
  const queryKey = ["design", parent.kind, parent.id];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      let design: any = null;
      if (parent.kind === "project") {
        const { data: d } = await supabase.from("project_designs").select("*").eq("project_id", parent.id).maybeSingle();
        design = d;
        if (!design) {
          // promoted from a BID? adopt its design on first save
          const { data: bids } = await supabase.from("bids").select("bid_id").eq("promoted_to_project_id", parent.id).limit(1);
          const bidId = (bids ?? [])[0]?.bid_id;
          if (bidId) {
            const { data: bd } = await supabase.from("project_designs").select("*").eq("bid_id", bidId).maybeSingle();
            design = bd;
          }
        }
      } else {
        const { data: d } = await supabase.from("project_designs").select("*").eq("bid_id", parent.id).maybeSingle();
        design = d;
      }
      if (!design) return { design: null, rf: [], ground: [], tx: [] };
      const [rf, ground, tx] = await Promise.all([
        supabase.from("design_rf_items").select("*").eq("design_id", design.design_id).order("sort_order"),
        supabase.from("design_ground_items").select("*").eq("design_id", design.design_id).order("sort_order"),
        supabase.from("design_tx_links").select("*").eq("design_id", design.design_id).order("sort_order"),
      ]);
      return { design, rf: (rf.data ?? []) as any[], ground: (ground.data ?? []) as any[], tx: (tx.data ?? []) as any[] };
    },
  });

  const fromDb = (): Draft => {
    const h: Record<string, string> = {};
    for (const f of HEADER_FIELDS) h[f.key] = s(data?.design?.[f.key]);
    return {
      header: h,
      rf: rowsFromDb(RF_COLS, data?.rf ?? []),
      ground: rowsFromDb(GROUND_COLS, data?.ground ?? []),
      tx: rowsFromDb(TX_COLS, data?.tx ?? []),
    };
  };

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [importNote, setImportNote] = useState<string | null>(null);
  const [importSource, setImportSource] = useState<"template" | "ai" | null>(null);
  const [rawImport, setRawImport] = useState<any>(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const view: Draft = useMemo(() => (editing && draft ? draft : fromDb()), [editing, draft, data]);  // eslint-disable-line react-hooks/exhaustive-deps

  function startEdit() { setDraft(fromDb()); setImportNote(null); setImportSource(null); setRawImport(null); setErr(null); setEditing(true); }
  function cancel() { setEditing(false); setDraft(null); setImportNote(null); setImportSource(null); setRawImport(null); setErr(null); }

  function applyImport(partial: Partial<Draft>, note: string, source: "template" | "ai", raw: any) {
    const base = fromDb();
    const merged: Draft = {
      header: { ...base.header, ...Object.fromEntries(Object.entries(partial.header ?? {}).filter(([, v]) => v !== "")) },
      rf: partial.rf && partial.rf.length ? partial.rf : base.rf,
      ground: partial.ground && partial.ground.length ? partial.ground : base.ground,
      tx: partial.tx && partial.tx.length ? partial.tx : base.tx,
    };
    setDraft(merged);
    setImportNote(`${note}: ${partial.rf?.length ?? 0} RF rows, ${partial.ground?.length ?? 0} ground, ${partial.tx?.length ?? 0} Tx. Review below, then Save.`);
    setImportSource(source);
    setRawImport(raw);
    setErr(null);
    setEditing(true);
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = (name: string) => wb.Sheets[wb.SheetNames.find(n => norm(n) === norm(name)) ?? ""];
      const partial: Partial<Draft> = {
        header: siteSheetToHeader(sheet("Site")),
        rf: sheetToRows(RF_COLS, sheet("RF")),
        ground: sheetToRows(GROUND_COLS, sheet("Ground")),
        tx: sheetToRows(TX_COLS, sheet("Tx")),
      };
      if (!partial.rf?.length && !partial.ground?.length && !partial.tx?.length && !Object.keys(partial.header ?? {}).length) {
        setErr("That file didn't have RF / Ground / Tx sheets I recognise. Use the downloaded template (don't rename sheets or headers).");
        return;
      }
      applyImport(partial, `Imported ${f.name}`, "template", { file: f.name, sheets: wb.SheetNames });
    } catch (ex: any) {
      setErr(ex?.message ?? "Couldn't read that file.");
    }
  }

  function onPaste() {
    const obj = extractJson(pasteText);
    const res = obj ? jsonToDraft(obj) : null;
    if (!res) {
      setErr("I couldn't find a Coretel scope/design JSON block in that text. Paste the whole summary including the ```json footer.");
      return;
    }
    setPasteOpen(false);
    setPasteText("");
    applyImport(res.draft, `Pasted ${res.note}`, "ai", obj);
  }

  async function save() {
    if (!draft) return;
    setSaving(true); setErr(null);
    try {
      const header: Record<string, any> = {};
      for (const f of HEADER_FIELDS) {
        const raw = (draft.header[f.key] ?? "").trim();
        if (f.kind === "num" || f.kind === "int") {
          const n = raw === "" ? NaN : Number(raw.replace(/[^0-9.\-]/g, ""));
          header[f.key] = Number.isFinite(n) ? (f.kind === "int" ? Math.round(n) : n) : null;
        } else if (f.kind === "date") {
          const iso = toIsoDate(raw);
          header[f.key] = iso || null;
        } else header[f.key] = raw === "" ? null : raw;
      }
      if (importSource) { header.source = importSource; header.raw_import = rawImport; }

      let designId: string | undefined = data?.design?.design_id;
      if (!designId) {
        const ins: any = { ...header, site_id: parent.siteId ?? null, source: importSource ?? "manual" };
        if (parent.kind === "project") ins.project_id = parent.id; else ins.bid_id = parent.id;
        const { data: d, error } = await supabase.from("project_designs").insert(ins).select("design_id").single();
        if (error) throw error;
        designId = (d as any).design_id;
      } else {
        const upd: any = { ...header };
        // adopt a BID's design onto the promoted project
        if (parent.kind === "project" && !data?.design?.project_id) upd.project_id = parent.id;
        if (!data?.design?.site_id && parent.siteId) upd.site_id = parent.siteId;
        const { error } = await supabase.from("project_designs").update(upd).eq("design_id", designId);
        if (error) throw error;
      }

      const replace = async (table: string, cols: Col[], rows: Row[], fix?: (o: any) => void) => {
        const { error: delErr } = await supabase.from(table as any).delete().eq("design_id", designId);
        if (delErr) throw delErr;
        const payload = rows.filter(r => !rowIsEmpty(cols, r)).map((r, i) => {
          const o = typed(cols, r); o.design_id = designId; o.sort_order = i; fix?.(o); return o;
        });
        if (payload.length) {
          const { error } = await supabase.from(table as any).insert(payload);
          if (error) throw error;
        }
      };
      await replace("design_rf_items", RF_COLS, draft.rf);
      await replace("design_ground_items", GROUND_COLS, draft.ground, o => { if (!o.item_type) o.item_type = "other"; });
      await replace("design_tx_links", TX_COLS, draft.tx, o => { if (!o.medium) o.medium = "microwave"; });

      await qc.invalidateQueries({ queryKey });
      setEditing(false); setDraft(null); setImportNote(null); setImportSource(null); setRawImport(null);
    } catch (ex: any) {
      setErr(ex?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  // ---- glance counts (client-side, mirrors v_design_summary) ------------------
  const glance = useMemo(() => {
    const n = (v: string, d = 1) => { const x = Number(v); return Number.isFinite(x) && v !== "" ? x : d; };
    const rf = view.rf, tx = view.tx, g = view.ground;
    return {
      antennas: rf.filter(r => r.antenna_model && r.works !== "REMOVE").reduce((a, r) => a + n(r.qty), 0),
      removed: rf.filter(r => r.antenna_model && r.works === "REMOVE").reduce((a, r) => a + n(r.qty), 0),
      rrus: rf.filter(r => r.works !== "REMOVE").reduce((a, r) => a + n(r.rru_qty, 0), 0),
      sectors: new Set(rf.map(r => r.sector).filter(Boolean)).size || n(view.header.sector_count, 0),
      mw: tx.filter(t => t.medium !== "fibre" && t.works !== "REMOVE").length,
      fibre: tx.filter(t => t.medium === "fibre").length,
      cabinets: g.filter(x => (x.item_type === "cabinet" || x.item_type === "shelter") && x.works !== "REMOVE").reduce((a, x) => a + n(x.qty), 0),
      strings: g.filter(x => (x.item_type === "battery_string" || x.item_type === "battery_rack") && x.works !== "REMOVE").reduce((a, x) => a + n(x.strings, n(x.qty, 0)), 0),
    };
  }, [view]);

  const hasAnything = !!data?.design || editing;
  const hdr = view.header;
  const factOf = (k: string) => (editing ? draft?.header[k] ?? "" : hdr[k] ?? "");
  const setHdr = (k: string, v: string) => setDraft(d => d && ({ ...d, header: { ...d.header, [k]: v } }));

  if (isLoading) return <Card><div className="p-6 flex justify-center"><Spinner /></div></Card>;

  // Layout, top to bottom (31 Aug 2026, James: "tables up top so we are not
  // lost in all this text... handy to have but not at the top of the screen
  // or nobody will read all that"):
  //   header + actions -> glance tiles -> key facts + latest FC/SID ->
  //   scope (3 lines, expandable) -> RF / Tx / Ground tables -> notes
  //   accordion (sector naming, layout, power, civil) collapsed.
  // NOTE: no inline wrapper component - it would remount inputs every keystroke.
  return (
    <div className={embedded ? "" : "space-y-4"}>
      <Card>
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="text-[12px] font-medium text-text-muted uppercase tracking-wider flex items-center gap-1.5">
                <DraftingCompass size={13} /> Site design
              </div>
              {data?.design && !editing && (
                <div className="text-[11px] text-text-dim mt-1 truncate max-w-[70ch]"
                  title={[data.design.design_ref, data.design.design_rev, data.design.design_date].filter(Boolean).join(" · ")}>
                  {[data.design.design_ref, data.design.design_rev && `Rev ${data.design.design_rev}`, data.design.design_date && fmtShortDate(data.design.design_date)]
                    .filter(Boolean).join(" · ") || "No design reference recorded"}
                  {" · "}<span className="capitalize">{data.design.source === "ai" ? "pasted summary" : data.design.source}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {canEdit && !editing && (
                <>
                  <Button size="sm" variant="secondary" icon={FileSpreadsheet} onClick={() => downloadTemplate(parent, fromDb())}
                    title="Excel template, pre-filled with what's already here. Fill it by hand or hand it to an AI with the design docs.">
                    Template
                  </Button>
                  <Button size="sm" variant="secondary" icon={Upload} onClick={() => fileRef.current?.click()} title="Import a filled template">
                    Import Excel
                  </Button>
                  <Button size="sm" variant="secondary" icon={Clipboard} onClick={() => { setPasteOpen(v => !v); setErr(null); }}
                    title="Paste the Coretel Site Scope Summary from your AI chat">
                    Paste summary
                  </Button>
                  <Button size="sm" variant="secondary" icon={Pencil} onClick={startEdit}>{data?.design ? "Edit" : "Enter manually"}</Button>
                  <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} />
                </>
              )}
              {editing && (
                <>
                  <Button size="sm" variant="secondary" icon={X} onClick={cancel} disabled={saving}>Cancel</Button>
                  <Button size="sm" variant="primary" icon={Save} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save design"}</Button>
                </>
              )}
            </div>
          </div>

          {pasteOpen && !editing && (
            <div className="mt-3 border border-line rounded-md p-3 bg-ink-raised/30">
              <div className="text-[12px] text-text-muted mb-2">
                Paste the whole Site Scope Summary (the one your Projects assistant produces from the FCs) — including the <span className="font-mono">```json</span> footer.
              </div>
              <Textarea rows={6} value={pasteText} onChange={e => setPasteText(e.target.value)} placeholder="### 1. Site … ```json { &quot;schema&quot;: &quot;coretel.scope.v1&quot; … } ```" />
              <div className="flex justify-end gap-2 mt-2">
                <Button size="sm" variant="secondary" onClick={() => { setPasteOpen(false); setPasteText(""); }}>Close</Button>
                <Button size="sm" variant="primary" icon={Upload} onClick={onPaste} disabled={!pasteText.trim()}>Load for review</Button>
              </div>
            </div>
          )}

          {importNote && <div className="mt-3 text-[12px] text-ok border border-ok/40 bg-ok/5 rounded px-3 py-2">{importNote}</div>}
          {err && <div className="mt-3 text-[12px] text-danger border border-danger/40 bg-danger/5 rounded px-3 py-2">{err}</div>}

          {!hasAnything ? (
            <div className="mt-4 text-[13px] text-text-muted">
              No design recorded yet. {canEdit
                ? "Three ways in: download the template and fill it (or let an AI fill it from the FCs), paste the scope summary, or enter it by hand."
                : "The design will show here once a manager loads it."}
            </div>
          ) : (
            <>
              {/* glance */}
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                <Glance label="Sectors" value={glance.sectors} />
                <Glance label="Antennas" value={glance.antennas} sub={glance.removed ? `${glance.removed} removed` : undefined} />
                <Glance label="RRUs" value={glance.rrus} />
                <Glance label="MW links" value={glance.mw} />
                <Glance label="Fibre" value={glance.fibre} />
                <Glance label="Cabinets" value={glance.cabinets} />
                <Glance label="Battery strings" value={glance.strings} />
              </div>

              {/* key facts (short values only) */}
              <div className="mt-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-x-4 gap-y-3">
                  {HEADER_FIELDS.filter(f => !f.wide).map(f => (
                    <div key={f.key} className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-text-muted">{f.label}</div>
                      {editing ? (
                        <input value={factOf(f.key)} onChange={e => setHdr(f.key, e.target.value)}
                          type={f.kind === "date" ? "date" : "text"} inputMode={f.kind === "num" || f.kind === "int" ? "decimal" : undefined}
                          className="mt-1 w-full bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text focus:outline-none focus:border-accent" />
                      ) : (
                        <div className="text-[13px] text-text mt-0.5 truncate" title={hdr[f.key] || undefined}>
                          {hdr[f.key]
                            ? (f.kind === "date" ? fmtShortDate(hdr[f.key]) : f.key === "structure_height_m" ? `${hdr[f.key]} m` : hdr[f.key])
                            : <span className="text-text-dim">—</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* the latest document for each discipline - one place, always current */}
              {!editing && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5">Latest design documents</div>
                  <LatestDocs parent={parent} />
                </div>
              )}

              {/* scope - the one paragraph worth reading before the tables */}
              {(editing || hdr.summary) && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-wider text-text-muted">Scope in one breath</div>
                  {editing ? (
                    <textarea rows={3} value={factOf("summary")} onChange={e => setHdr("summary", e.target.value)}
                      className="mt-1 w-full bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text focus:outline-none focus:border-accent resize-y" />
                  ) : (
                    <Clamp text={hdr.summary} lines={3} className="text-[13px] text-text mt-1" />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Card>

      {hasAnything && (
        <>
          <div className={wide ? "space-y-4 xl:-mx-24 2xl:-mx-40" : "space-y-4"}>
          <Section icon={RadioTower} title="RF — antennas & radios" cols={RF_COLS} rows={view.rf} editing={editing}
            groupBy="sector"
            onChange={rows => setDraft(d => d && ({ ...d, rf: rows }))}
            addDefaults={{ works: "NEW", qty: "1" }} emptyText="No RF lines yet." embedded={embedded} />
          <Section icon={Cable} title="Transmission — microwave & fibre" cols={TX_COLS} rows={view.tx} editing={editing}
            onChange={rows => setDraft(d => d && ({ ...d, tx: rows }))}
            addDefaults={{ medium: "microwave", works: "NEW" }} emptyText="No transmission links yet." embedded={embedded} />
          <Section icon={Server} title="Ground — cabinets, shelters, batteries, power" cols={GROUND_COLS} rows={view.ground} editing={editing}
            onChange={rows => setDraft(d => d && ({ ...d, ground: rows }))}
            addDefaults={{ item_type: "cabinet", works: "NEW", qty: "1" }} emptyText="No ground equipment yet." embedded={embedded} />
          </div>

          {/* the long-form notes - present, searchable, out of the way */}
          {(editing || NOTE_FIELDS.some(f => hdr[f.key])) && (
            <Card>
              <div className="p-5">
                <div className="text-[12px] font-medium text-text-muted uppercase tracking-wider flex items-center gap-1.5">
                  <FileText size={13} /> Design notes
                  {!editing && (
                    <span className="text-text-dim normal-case tracking-normal font-normal">
                      · {NOTE_FIELDS.filter(f => hdr[f.key]).length} of {NOTE_FIELDS.length} filled
                    </span>
                  )}
                </div>
                <div className={`mt-3 ${editing ? "grid grid-cols-1 md:grid-cols-2 gap-4" : "divide-y divide-line"}`}>
                  {NOTE_FIELDS.map(f => (
                    editing ? (
                      <div key={f.key}>
                        <div className="text-[10px] uppercase tracking-wider text-text-muted">{f.label}</div>
                        <textarea rows={4} value={factOf(f.key)} onChange={e => setHdr(f.key, e.target.value)}
                          className="mt-1 w-full bg-ink border border-line rounded px-2 py-1.5 text-[12px] text-text focus:outline-none focus:border-accent resize-y" />
                      </div>
                    ) : hdr[f.key] ? (
                      <NoteRow key={f.key} label={f.label} text={hdr[f.key]} />
                    ) : null
                  ))}
                </div>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// The long-form header text lives here in view mode (collapsed by default).
const NOTE_FIELDS = HEADER_FIELDS.filter(f => f.wide && f.key !== "summary");

function fmtShortDate(iso: string) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

function Glance({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <div className="rounded-md border border-line bg-ink-raised/30 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className="text-[18px] font-semibold font-mono leading-tight mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-text-dim">{sub}</div>}
    </div>
  );
}

// Text that shows N lines and a "Show more" - for scope and notes.
function Clamp({ text, lines, className = "" }: { text: string; lines: number; className?: string }) {
  const [open, setOpen] = useState(false);
  const long = text.split("\n").length > lines || text.length > lines * 140;
  return (
    <div className={className}>
      <div className="whitespace-pre-wrap" style={open || !long ? undefined : { display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
        {text}
      </div>
      {long && (
        <button type="button" onClick={() => setOpen(v => !v)}
          className="mt-1 text-[11px] text-accent hover:text-accent-bright inline-flex items-center gap-1">
          {open ? <><ChevronDown size={11} className="rotate-180" /> Show less</> : <><ChevronDown size={11} /> Show more</>}
        </button>
      )}
    </div>
  );
}

function NoteRow({ label, text }: { label: string; text: string }) {
  const [open, setOpen] = useState(false);
  const firstLine = text.split("\n").find(l => l.trim()) ?? "";
  return (
    <div className="py-2.5">
      <button type="button" onClick={() => setOpen(v => !v)} className="w-full text-left flex items-start gap-2">
        <ChevronRight size={13} className={`mt-0.5 flex-shrink-0 text-text-dim transition-transform ${open ? "rotate-90" : ""}`} />
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-medium text-text">{label}</div>
          {!open && <div className="text-[12px] text-text-muted truncate">{firstLine}</div>}
        </div>
      </button>
      {open && <div className="mt-2 pl-5 text-[13px] text-text whitespace-pre-wrap leading-relaxed">{text}</div>}
    </div>
  );
}

// ---- Latest FC / SID off the job's documents (T-DES-DOCS) ---------------------
// James: "grab the latest FCs loaded into documents and the latest Site
// Installation Document instead of a big document library - one place the
// latest document of that specific thing is." Matched on the document's
// subcategory or title/file name; newest upload wins. Field sees whatever the
// document RLS already lets them see.
// 1 Sep 2026 - James: "RF and Tx can have their own documents - a Tx DD / LER,
// and RF have CSTD (Telstra) or RF Schematic (NBN)... and add in Ground which
// captures the rest." One card per discipline; each shows the newest match.
const DOC_KINDS: { key: string; label: string; hint: string; test: RegExp }[] = [
  { key: "fc",     label: "Latest FC",   hint: "FC / for construction",
    test: /\bFC\b|final\s*construction|construction\s*(drawing|issue)|for\s*construction|AAFC/i },
  { key: "sid",    label: "Latest SID",  hint: "SID / site installation",
    test: /\bSID\b|site\s*installation/i },
  { key: "rf",     label: "RF design",   hint: "CSTD (Telstra) / RF schematic (NBN)",
    test: /\bCSTD\b|RF\s*(schematic|design|data|config)|antenna\s*(schedule|config|line)|\bRFDS\b|\bRND\b/i },
  { key: "tx",     label: "Tx design",   hint: "DD / LER / link design",
    test: /\bDD\b|\bLER\b|link\s*(engineering|design|budget)|transmission\s*design|\bMW\b.*design|microwave\s*design|path\s*profile/i },
  { key: "ground", label: "Ground / power", hint: "SLD / power / earthing / civil / site layout",
    test: /\bSLD\b|single\s*line|power\s*(design|drawing|schematic)|earthing|electrical\s*design|site\s*layout|civil\s*(design|drawing)|structural/i },
];

function LatestDocs({ parent }: { parent: DesignParent }) {
  const { data: docs = [] } = useQuery({
    queryKey: ["design_latest_docs", parent.kind, parent.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("bid_documents")
        .select("id, file_name, file_path, title, mime_type, uploaded_at, doc_type, sub:subcategory_id ( code, display_name )")
        .eq(parent.kind === "project" ? "project_id" : "bid_id", parent.id)
        .order("uploaded_at", { ascending: false })
        .limit(300);
      return (data ?? []) as any[];
    },
  });
  const picks = DOC_KINDS.map(k => ({
    ...k,
    doc: docs.find((d: any) => {
      const hay = [d.title, d.file_name, d.doc_type, d.sub?.code, d.sub?.display_name].filter(Boolean).join(" | ");
      return k.test.test(hay);
    }),
  }));

  async function openDoc(doc: any) {
    // Open the window synchronously (popup blockers), navigate once signed.
    // 9 Sep: "noopener" makes window.open return NULL — tab opened but could
    // never be navigated (blank page on phones). No features = handle back;
    // sever opener by hand.
    const win = window.open("", "_blank");
    if (win) win.opener = null;
    const { data, error } = await supabase.storage.from("bid-documents").createSignedUrl(doc.file_path, 60 * 10);
    if (error || !data?.signedUrl) { if (win) win.close(); alert(error?.message ?? "Could not load document"); return; }
    const inlineable = (doc.mime_type ?? "") === "application/pdf" || String(doc.mime_type ?? "").startsWith("image/");
    const url = inlineable ? `${data.signedUrl}&response-content-disposition=inline` : data.signedUrl;
    if (win) win.location.href = url; else window.open(url, "_blank");
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {picks.map(p => (
        <div key={p.key} className={`rounded-md border px-3 py-2 min-w-0 ${p.doc ? "border-line bg-ink-raised/30" : "border-dashed border-line/70"}`}>
          <div className="text-[10px] uppercase tracking-wider text-text-muted flex items-center justify-between gap-2">
            <span>{p.label}</span>
            {p.doc && <span className="normal-case tracking-normal text-text-dim">{fmtShortDate(p.doc.uploaded_at)}</span>}
          </div>
          {p.doc ? (
            <button type="button" onClick={() => openDoc(p.doc)} className="mt-1 text-left w-full group" title={p.doc.title || p.doc.file_name}>
              <div className="text-[12px] text-accent group-hover:text-accent-bright truncate inline-flex items-center gap-1 max-w-full">
                <FileText size={12} className="flex-shrink-0" /><span className="truncate">{p.doc.title || p.doc.file_name}</span>
              </div>
              {p.doc.sub?.display_name && <div className="text-[10px] text-text-dim truncate">{p.doc.sub.display_name}</div>}
            </button>
          ) : (
            <div className="mt-1 text-[10px] text-text-dim leading-snug">None yet · {p.hint}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---- one editable / viewable table -------------------------------------------
function Section({ icon: Icon, title, cols, rows, editing, onChange, addDefaults, emptyText, groupBy, embedded }: {
  icon: any; title: string; cols: Col[]; rows: Row[]; editing: boolean;
  onChange: (rows: Row[]) => void; addDefaults: Record<string, string>; emptyText: string; groupBy?: string; embedded?: boolean;
}) {
  const visibleCols = editing ? cols : cols.filter(c => rows.some(r => (r[c.key] ?? "") !== ""));
  // View mode groups by sector: Telstra sheets list S1,S2,S3,S1,S2,S3... so
  // sort (stably) by sector for display, keeping the sheet's order within a
  // sector. Edit mode keeps the raw order so rows don't jump while typing.
  const shown: Row[] = (!editing && groupBy)
    ? rows.map((r, i) => ({ r, i })).sort((a, b) => {
        const ga = a.r[groupBy] ?? "", gb = b.r[groupBy] ?? "";
        return ga === gb ? a.i - b.i : ga.localeCompare(gb, undefined, { numeric: true });
      }).map(x => x.r)
    : rows;
  const set = (k: string, key: string, v: string) => onChange(rows.map(r => (r._k === k ? { ...r, [key]: v } : r)));
  const add = () => {
    const last = rows[rows.length - 1];
    const d = { ...addDefaults };
    if (groupBy && last?.[groupBy]) d[groupBy] = last[groupBy];
    onChange([...rows, blankRow(cols, d)]);
  };
  const remove = (k: string) => onChange(rows.filter(r => r._k !== k));
  const dup = (k: string) => {
    const i = rows.findIndex(r => r._k === k);
    if (i < 0) return;
    const copy = { ...rows[i], _k: newKey() };
    onChange([...rows.slice(0, i + 1), copy, ...rows.slice(i + 1)]);
  };
  // Group header rows only when a group actually has more than one line -
  // NBN names every antenna as its own sector, so headers there would just
  // double the row count.
  const groupCounts = new Map<string, number>();
  if (groupBy) for (const r of rows) groupCounts.set(r[groupBy] ?? "", (groupCounts.get(r[groupBy] ?? "") ?? 0) + 1);

  const body = (
    <div className={embedded ? "mt-4" : "p-5"}>
      <div className="flex items-center justify-between">
        <div className="text-[12px] font-medium text-text-muted uppercase tracking-wider flex items-center gap-1.5">
          <Icon size={13} /> {title}
          <span className="text-text-dim normal-case tracking-normal font-normal">· {rows.length}</span>
        </div>
        {editing && <Button size="sm" variant="secondary" icon={Plus} onClick={add}>Add row</Button>}
      </div>
      {rows.length === 0 && !editing ? (
        <div className="text-[12px] text-text-dim mt-2">{emptyText}</div>
      ) : (
        <div className="mt-2 overflow-x-auto -mx-1 px-1">
          <table className="text-[12px] min-w-full">
            <thead>
              <tr className="border-b border-line text-[10px] uppercase tracking-wider text-text-muted">
                {visibleCols.map(c => <th key={c.key} className={`px-1.5 py-1.5 whitespace-nowrap ${isNumCol(c) ? "text-right" : "text-left"}`}>{c.label}</th>)}
                {editing && <th className="w-16"></th>}
              </tr>
            </thead>
            <tbody>
              {shown.map((r, i) => {
                const g = groupBy ? (r[groupBy] ?? "") : "";
                const groupStart = !!groupBy && !editing && (groupCounts.get(g) ?? 0) > 1 && (i === 0 || shown[i - 1][groupBy!] !== g);
                return (
                  <React.Fragment key={r._k}>
                    {groupStart && (
                      <tr className="bg-ink-raised/40">
                        <td colSpan={visibleCols.length} className="px-1.5 py-1 text-[10px] uppercase tracking-wider text-text-muted">
                          Sector {g || "—"} <span className="text-text-dim normal-case tracking-normal">· {groupCounts.get(g)} lines</span>
                        </td>
                      </tr>
                    )}
                    <tr className={`border-b border-line/50 ${!editing ? "hover:bg-ink-raised/30" : ""}`}>
                      {visibleCols.map(c => (
                        <td key={c.key} className={`px-1.5 py-1.5 align-top ${isNumCol(c) && !editing ? "text-right" : ""}`}>
                          {editing ? (
                            c.kind === "select" ? (
                              <select value={r[c.key] ?? ""} onChange={e => set(r._k, c.key, e.target.value)}
                                className={`${c.w ?? "w-24"} bg-ink border border-line rounded px-1 py-1 text-[12px] text-text focus:outline-none focus:border-accent`}>
                                <option value="">—</option>
                                {(c.opts ?? []).map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                            ) : c.kind === "bool" ? (
                              <select value={r[c.key] ?? ""} onChange={e => set(r._k, c.key, e.target.value)}
                                className={`${c.w ?? "w-16"} bg-ink border border-line rounded px-1 py-1 text-[12px] text-text focus:outline-none focus:border-accent`}>
                                <option value="">—</option><option value="yes">yes</option><option value="no">no</option>
                              </select>
                            ) : (
                              <input value={r[c.key] ?? ""} onChange={e => set(r._k, c.key, e.target.value)}
                                inputMode={c.kind === "num" || c.kind === "int" ? "decimal" : undefined}
                                className={`${c.w ?? "w-24"} bg-ink border border-line rounded px-1.5 py-1 text-[12px] text-text focus:outline-none focus:border-accent ${c.mono ? "font-mono" : ""}`} />
                            )
                          ) : (
                            <ViewCell c={c} v={r[c.key] ?? ""} />
                          )}
                        </td>
                      ))}
                      {editing && (
                        <td className="px-1 py-1 whitespace-nowrap">
                          <button type="button" onClick={() => dup(r._k)} title="Duplicate row" className="text-text-dim hover:text-text px-1"><Plus size={13} /></button>
                          <button type="button" onClick={() => remove(r._k)} title="Remove row" className="text-text-dim hover:text-danger px-1"><Trash2 size={13} /></button>
                        </td>
                      )}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
  return embedded ? body : <Card>{body}</Card>;
}

const isNumCol = (c: Col) => c.kind === "num" || c.kind === "int";

// How a value reads in view mode: works as a pill, azimuth with a bearing
// arrow, heights in metres, tilts in degrees, long notes truncated with the
// full text on hover, sector as a mono tag.
function ViewCell({ c, v }: { c: Col; v: string }) {
  if (!v) return <span className="text-text-dim">—</span>;
  if (c.key === "works") {
    return <span className={`inline-block px-1.5 py-[1px] rounded text-[10px] font-medium tracking-wide ${worksPill(v)}`}>{v}</span>;
  }
  if (c.key === "azimuth_deg") {
    const deg = Number(v);
    return (
      <span className="inline-flex items-center justify-end gap-1 font-mono whitespace-nowrap">
        {Number.isFinite(deg) && (
          <svg width="11" height="11" viewBox="0 0 12 12" className="text-text-muted flex-shrink-0" style={{ transform: `rotate(${deg}deg)` }} aria-hidden>
            <path d="M6 1 L8.5 8 L6 6.5 L3.5 8 Z" fill="currentColor" />
          </svg>
        )}
        {v}°
      </span>
    );
  }
  if (c.key === "height_m" || c.key === "structure_height_m") return <span className="font-mono whitespace-nowrap">{v} m</span>;
  if (c.key === "dish_m") return <span className="font-mono whitespace-nowrap">{v} m</span>;
  if (c.key === "mech_tilt" || c.key === "elec_tilt") return <span className="font-mono whitespace-nowrap">{v}{/^-?[\d.]+$/.test(v) ? "°" : ""}</span>;
  if (c.key === "sector") return <span className="font-mono text-[11px] px-1.5 py-[1px] rounded bg-ink-raised border border-line/70">{v}</span>;
  if (c.key === "antenna_model" || c.key === "link_name" || c.key === "model") return <span className="font-medium text-text whitespace-nowrap">{v}</span>;
  if (c.key === "notes") return <span className="block max-w-[200px] truncate text-text-muted" title={v}>{v}</span>;
  if (isNumCol(c)) return <span className="font-mono whitespace-nowrap">{v}</span>;
  return <span className="whitespace-nowrap">{v}</span>;
}

function worksPill(w: string) {
  switch (w) {
    case "NEW":      return "bg-ok/15 text-ok";
    case "REMOVE":   return "bg-danger/15 text-danger";
    case "RELOCATE": case "REPLACE": return "bg-warn/15 text-warn";
    case "RETAIN":   return "bg-ink-raised text-text-dim";
    default:         return "bg-ink-raised text-text-muted";
  }
}


// =============================================================================
// Shared: load a design (project first, then its source BID) + its items.
// Used by the Design tab itself, the HOP wizard pre-fill and the quote
// builder's glance card (T-SCOPE-3, 1 Sep 2026).
// =============================================================================
export type DesignBundle = {
  design: any | null;
  rf: any[]; ground: any[]; tx: any[];
};

export async function loadDesignBundle(opts: { projectId?: string | null; bidId?: string | null }): Promise<DesignBundle> {
  let design: any = null;
  if (opts.projectId) {
    const { data } = await supabase.from("project_designs").select("*").eq("project_id", opts.projectId).maybeSingle();
    design = data;
    if (!design) {
      const { data: bids } = await supabase.from("bids").select("bid_id").eq("promoted_to_project_id", opts.projectId).limit(1);
      const bidId = (bids ?? [])[0]?.bid_id;
      if (bidId) {
        const { data: bd } = await supabase.from("project_designs").select("*").eq("bid_id", bidId).maybeSingle();
        design = bd;
      }
    }
  }
  if (!design && opts.bidId) {
    const { data } = await supabase.from("project_designs").select("*").eq("bid_id", opts.bidId).maybeSingle();
    design = data;
  }
  if (!design) return { design: null, rf: [], ground: [], tx: [] };
  const [rf, ground, tx] = await Promise.all([
    supabase.from("design_rf_items").select("*").eq("design_id", design.design_id).order("sort_order"),
    supabase.from("design_ground_items").select("*").eq("design_id", design.design_id).order("sort_order"),
    supabase.from("design_tx_links").select("*").eq("design_id", design.design_id).order("sort_order"),
  ]);
  return { design, rf: rf.data ?? [], ground: ground.data ?? [], tx: tx.data ?? [] };
}

// What the wizard / quote actually want off a design, pre-chewed.
// localHints = names for the site we're standing on (site code, project
// name...). A link's FAR END is whichever of us/ds ISN'T us - 2 Sep 2026,
// James on Sharon: every link had "Sharon" as the US site, so "uWave facing
// Sharon" x4 was the site itself. With no hint, prefer the DS site.
export function designDigest(b: DesignBundle, localHints: (string | null | undefined)[] = []) {
  const normHint = (v: any) => String(v ?? "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const hints = localHints.map(normHint).filter(h => h.length >= 3);
  const isLocal = (v: any) => {
    const n = normHint(v);
    if (!n) return false;
    return hints.some(h => h.includes(n) || n.includes(h));
  };
  const farEndOf = (l: any): string | null => {
    const us = l.us_site ?? null, ds = l.ds_site ?? null;
    if (us && ds) {
      if (isLocal(us) && !isLocal(ds)) return ds;
      if (isLocal(ds) && !isLocal(us)) return us;
      return ds;                       // can't tell - DS is the far end by convention
    }
    const one = ds ?? us;
    return one && !isLocal(one) ? one : (one ?? null);
  };
  // The label a rigger wants on the photo slot: the link_name with the local
  // site stripped ("Sharon - Sharon South (Main)" -> "Sharon South Main"),
  // else "uWave facing <far end>".
  const slotLabel = (l: any, farEnd: string | null): string => {
    let name = String(l.link_name ?? "").trim();
    // "Sharon - Sharon South (Main)" -> keep from the far end onward:
    // "Sharon South Main". Works whatever the local site is called.
    if (name && farEnd) {
      const i = name.toLowerCase().indexOf(farEnd.toLowerCase());
      if (i > 0) name = name.slice(i);
    }
    if (name) {
      for (const h of localHints.filter(Boolean) as string[]) {
        name = name.replace(new RegExp(`^\\s*${h.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*[-–—>]+\\s*`, "i"), "");
      }
      name = name.replace(/[()]/g, " ").replace(/\s+/g, " ").trim();
      if (name) return name;
    }
    return farEnd ? `uWave facing ${farEnd}` : "";
  };
  return designDigestInner(b, farEndOf, slotLabel);
}

function designDigestInner(b: DesignBundle, farEndOf: (l: any) => string | null, slotLabel: (l: any, farEnd: string | null) => string) {
  const live = (w: any) => w !== "REMOVE";
  const rf = b.rf.filter(r => live(r.works));
  const sectors = [...new Set(rf.map(r => r.sector).filter(Boolean))] as string[];
  const activeAntennas = rf.filter(r => r.antenna_model && r.active_passive === "active");
  const antennas = rf.filter(r => r.antenna_model);
  const mw = b.tx.filter(l => l.medium !== "fibre" && live(l.works));
  const cab = (types: string[]) => b.ground.filter(g => types.includes(g.item_type) && live(g.works));
  const cabinets = cab(["cabinet"]);
  const rbs = cabinets.filter(c => /\brbs\b/i.test(String(c.model ?? "")));
  const plainCabinets = cabinets.filter(c => !/\brbs\b/i.test(String(c.model ?? "")));
  const shelters = cab(["shelter"]);
  const strings = b.ground.filter(g => (g.item_type === "battery_string" || g.item_type === "battery_rack") && live(g.works));
  const sumQty = (rows: any[]) => rows.reduce((a, r) => a + (Number(r.qty) || 1), 0);
  return {
    hasDesign: !!b.design,
    summary: b.design?.summary ?? null,
    sectors,
    sectorNaming: b.design?.sector_naming ?? null,
    antennaCount: sumQty(antennas),
    activeAntennaCount: sumQty(activeAntennas),
    mwLinks: mw.map(l => ({
      name: l.link_name || null,
      farEnd: farEndOf(l),
      dish: l.dish_m, band: l.freq_band,
    })),
    // One PHOTO SLOT per dish, not per logical link: dual-band links to the
    // same far end share a dish and collapse to "<far end> Dual Band".
    mwInstances: (() => {
      const out: { label: string }[] = [];
      const dualDone = new Set<string>();
      for (const l of mw) {
        const fe = farEndOf(l);
        const key = (fe ?? l.link_name ?? "link").toLowerCase();
        if (l.dual_band) {
          if (dualDone.has(key)) continue;
          dualDone.add(key);
          out.push({ label: fe ? `${fe} Dual Band` : slotLabel(l, fe) });
        } else {
          out.push({ label: slotLabel(l, fe) });
        }
      }
      return out;
    })(),
    cabinetCount: sumQty(plainCabinets),
    rbsCount: sumQty(rbs),
    shelterCount: sumQty(shelters),
    cabinetNames: cabinets.map(c => c.model).filter(Boolean) as string[],
    batteryStrings: strings.reduce((a, g) => a + (Number(g.strings) || Number(g.qty) || 0), 0),
  };
}

// Compact read-only card — the design at a glance wherever the full tab is
// too much (quote builder, HOP wizard). Renders nothing when there is no
// design, so callers can drop it in unconditionally.
export function DesignGlanceCard({ projectId, bidId, linkTo, localHints }: {
  projectId?: string | null; bidId?: string | null; linkTo?: string; localHints?: (string | null | undefined)[];
}) {
  const { data } = useQuery({
    queryKey: ["design_bundle", projectId ?? null, bidId ?? null],
    enabled: !!(projectId || bidId),
    queryFn: () => loadDesignBundle({ projectId, bidId }),
  });
  if (!data?.design) return null;
  const d = designDigest(data, localHints ?? []);
  const chip = (label: string, v: number | string) => (
    <span className="inline-flex items-baseline gap-1 rounded border border-line bg-ink-raised/40 px-2 py-0.5">
      <span className="font-mono font-semibold text-[12px]">{v}</span>
      <span className="text-[10px] uppercase tracking-wider text-text-muted">{label}</span>
    </span>
  );
  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[11px] font-medium text-text-muted uppercase tracking-wider flex items-center gap-1.5">
            <DraftingCompass size={12} /> Site design
            {data.design.design_rev && <span className="normal-case tracking-normal text-text-dim">Rev {data.design.design_rev}</span>}
          </div>
          {linkTo && (
            <a href={linkTo} className="text-[11px] text-accent hover:text-accent-bright">Open design →</a>
          )}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5 text-text">
          {chip("sectors", d.sectors.length)}
          {chip("antennas", d.antennaCount)}
          {d.mwLinks.length > 0 && chip("MW links", d.mwLinks.length)}
          {(d.cabinetCount + d.rbsCount + d.shelterCount) > 0 && chip("cabinets", d.cabinetCount + d.rbsCount + d.shelterCount)}
          {d.batteryStrings > 0 && chip("battery strings", d.batteryStrings)}
        </div>
        {d.mwLinks.length > 0 && (
          <div className="mt-1.5 text-[11px] text-text-muted">
            {d.mwLinks.map((l, i) => [l.name, l.farEnd && `→ ${l.farEnd}`, l.dish && `${l.dish} m`].filter(Boolean).join(" ")).filter(Boolean).join(" · ")}
          </div>
        )}
        {d.summary && <div className="mt-1.5 text-[12px] text-text-muted line-clamp-2" title={d.summary}>{d.summary}</div>}
      </div>
    </Card>
  );
}
